import cookies from 'js-cookie';

import { cookieNames } from '@monorepo/common';

// The server-issued claim/checkout GUID is the only authority over a job-credit
// reservation — the draft GUID is deliberately never accepted as one. That GUID used to
// live in React state alone, so a page reload dropped it while the draft cookie survived:
// the customer's own reservation then held the credit (and the draft's single active
// claim) until its 60-minute lease expired, and re-applying the code reported that it had
// "reached its usage limit". Persisting it per draft lets a reloaded page — or a second
// tab — resume or release the very same claim.
const STORAGE_KEY = 'cpgJobBenefitClaim';

export interface IHeldBenefitClaim {
  /** The draft that owns this claim; a new draft never inherits an older draft's claim. */
  draftGuid: string;
  claimCheckoutGuid: string;
  contactEmail?: string;
}

/** The stored claim for the current draft, or null when there is none to resume. */
export const readHeldBenefitClaim = (): IHeldBenefitClaim | null => {
  const draftGuid = cookies.get(cookieNames.tempJob);
  if (!draftGuid) {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored) as IHeldBenefitClaim;
    return parsed?.claimCheckoutGuid && parsed.draftGuid === draftGuid ? parsed : null;
  } catch {
    // Unreadable or disabled storage is not an error: the in-memory claim still applies.
    return null;
  }
};

export const clearHeldBenefitClaim = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
};

export const storeHeldBenefitClaim = (claim: IHeldBenefitClaim): void => {
  if (!claim.draftGuid || !claim.claimCheckoutGuid) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(claim));
  } catch {
    // Private mode / storage quota: the claim survives in memory for this page only.
    clearHeldBenefitClaim();
  }
};
